/*
 *  -----------------------------------------------------------------------
 *    <copyright file="details-expandable.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { useBoolean, useLocalState, useTranslation } from 'hooks';

import './details-expandable.scss';
import { DataReviewTableFields } from 'queries/generated/fragments.generated';
import { DataReviewType } from 'types/graphql-global-types';

const DetailsExpandable = ({
    label,
    onClose,
    table,
    customKey,
}: {
    label: string;
    onClose: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void | Function;
    table: DataReviewTableFields;
    customKey?: string;
}) => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const expanded = useBoolean(true);

    const source = state?.dataSources?.find((s: any) => s.id === table?.dataSource);

    return (
        <Accordion
            className="details-expandable"
            expanded={expanded.value}
            key={customKey}
            slotProps={{ heading: { component: 'div' } }}
        >
            <AccordionSummary>
                <div className="header-part">
                    <div className="left-part">
                        <FontAwesomeIcon
                            id="risk-details-chevron-button"
                            icon={['fal', expanded.value ? 'chevron-up' : 'chevron-down']}
                            className="fa-lg arrow-btn"
                            onClick={() => {
                                expanded.setValue(!expanded.value);
                            }}
                        />
                        <p className="label-details">{label}</p>
                    </div>
                    <div className="right-part">
                        <FontAwesomeIcon
                            id="risk-details-close-button"
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={onClose}
                            data-testid="close-dialog"
                        />
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails className="panel-details-table">
                <p className="details-table">
                    <span className="title"> {t('name')}:</span>
                    {table?.name}
                </p>
                <p className="details-table">
                    <span className="title"> {t('admin:baseData')}:</span>
                    {t(`admin:${table?.type}-REVIEW_TYPE`)}
                </p>
                {table?.type === DataReviewType.DATA ? (
                    <p className="details-table">
                        <span className="title"> {t('dataSource')}:</span>
                        {source?.name ?? table?.dataSource}
                    </p>
                ) : null}
                <p className="details-table">
                    <span className="title"> {t('description')}:</span>
                    {table?.description}
                </p>
            </AccordionDetails>
        </Accordion>
    );
};

export default DetailsExpandable;
